import { buildAuthHeaders } from "../forum/authHeaders";

const ROOT = "/api/global-search";

async function request(path, { method = "GET", body, signal, headers = {} } = {}) {
  const response = await fetch(`${ROOT}${path}`, {
    method,
    signal,
    headers: buildAuthHeaders({
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...headers,
    }),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.detail || `Erreur serveur (${response.status})`);
    error.status = response.status;
    error.code = payload?.code || "request_failed";
    error.payload = payload;
    throw error;
  }
  return payload;
}

export const loadSearchMeta = (signal) => request("/meta/", { signal });

function fallbackUuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function createSearchExecutionContext(action) {
  if (!["submit", "resume"].includes(action)) return null;
  const executionId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : fallbackUuid();
  return { action, executionId };
}

export function searchNavigationState(action) {
  const searchExecution = createSearchExecutionContext(action);
  return searchExecution ? { searchExecution } : undefined;
}

export function searchEternua(params, signal, historyContext) {
  const query = params instanceof URLSearchParams ? params.toString() : new URLSearchParams(params || {}).toString();
  const action = historyContext?.action;
  const executionId = String(historyContext?.executionId || "").trim();
  const historyHeaders = ["submit", "resume"].includes(action) && executionId
    ? {
        "X-Search-History-Action": action,
        "X-Search-Execution-Id": executionId,
      }
    : {};
  return request(`/results/${query ? `?${query}` : ""}`, { signal, headers: historyHeaders });
}

export function loadSuggestions(query, signal) {
  const params = new URLSearchParams({ q: String(query || "") });
  return request(`/suggestions/?${params.toString()}`, { signal });
}

export const loadSearchDocument = (id, signal) => request(`/documents/${encodeURIComponent(id)}/`, { signal });
export const loadFavorites = (signal) => request("/favorites/", { signal });
export const setFavorite = (id, favorite) => request(`/favorites/${encodeURIComponent(id)}/`, { method: "POST", body: { favorite } });
export const loadSavedSearches = (signal) => request("/saved-searches/", { signal });
export const createSavedSearch = (body, idempotencyKey) => request("/saved-searches/", {
  method: "POST",
  body,
  headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
});
export const updateSavedSearch = (id, body) => request(`/saved-searches/${encodeURIComponent(id)}/`, { method: "PATCH", body });
export const pauseSavedSearch = (id, lockVersion) => request(`/saved-searches/${encodeURIComponent(id)}/pause/`, { method: "POST", body: { lock_version: lockVersion } });
export const resumeSavedSearch = (id, lockVersion) => request(`/saved-searches/${encodeURIComponent(id)}/resume/`, { method: "POST", body: { lock_version: lockVersion } });
export const duplicateSavedSearch = (id, lockVersion, name) => request(`/saved-searches/${encodeURIComponent(id)}/duplicate/`, {
  method: "POST",
  body: { lock_version: lockVersion, ...(name ? { name } : {}) },
});
export const markSavedSearchViewed = (id, lockVersion) => request(`/saved-searches/${encodeURIComponent(id)}/mark-viewed/`, {
  method: "POST",
  body: { lock_version: lockVersion },
});
export const deleteSavedSearch = (id, lockVersion) => request(`/saved-searches/${encodeURIComponent(id)}/`, {
  method: "DELETE",
  headers: { "If-Match": `"${lockVersion}"` },
});
export const setSavedSearchAlert = (id, enabled, lockVersion) => request(`/saved-searches/${encodeURIComponent(id)}/alerts/`, {
  method: "POST",
  body: { enabled, lock_version: lockVersion },
});
export const previewSavedSearch = (id, signal) => request(`/saved-searches/${encodeURIComponent(id)}/preview/`, { method: "POST", signal });
export const testSavedSearchAlert = (id) => request(`/saved-searches/${encodeURIComponent(id)}/test-alert/`, { method: "POST" });
export const loadSavedSearchMatches = (id, signal) => request(`/saved-searches/${encodeURIComponent(id)}/matches/`, { signal });
export const loadSearchAlertsStatus = (signal) => request("/alerts/status/", { signal });
export const loadSearchAlertsSummary = (signal) => request("/alerts/summary/", { signal });

export const loadSearchHistory = (signal) => request("/history/", { signal });
export const deleteSearchHistoryEntry = (id) => request(`/history/${encodeURIComponent(id)}/`, { method: "DELETE" });
export const clearSearchHistory = () => request("/history/clear/", { method: "POST" });
export const loadSearchHistoryPreferences = (signal) => request("/history/preferences/", { signal });
export const updateSearchHistoryPreferences = (body) => request("/history/preferences/", { method: "PATCH", body });
export const loadSearchPrivacyExport = (signal) => request("/privacy/export/", { signal });
export const loadSearchPrivacyDeletionPreview = (signal) => request("/privacy/deletion-preview/", { signal });
export const deleteSearchPrivateData = () => request("/privacy/delete/", {
  method: "DELETE",
  body: { confirm: true },
});

export const FILTER_KEYS = [
  "category",
  "subcategory",
  "city",
  "postal_code",
  "period",
  "date_from",
  "date_to",
  "min_amount",
  "max_amount",
  "offer_type",
  "item_condition",
  "delivery_mode",
  "location_mode",
  "contract_type",
  "remote_mode",
  "experience_level",
  "availability",
  "min_salary",
  "max_salary",
  "destination",
  "start_date",
  "end_date",
  "budget_type",
  "activity",
  "care_type",
  "listing_kind",
  "accommodation_type",
  "capacity",
  "amenity",
  "radius",
  "lat",
  "lng",
];

export function searchParamsToSpecification(params) {
  const source = params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  const filters = {};
  FILTER_KEYS.forEach((key) => {
    const value = String(source.get(key) || "").trim();
    if (value) filters[key] = value;
  });
  const universes = source.getAll("universe").flatMap((value) => value.split(",")).map((value) => value.trim()).filter(Boolean);
  return {
    schema_version: 2,
    text: String(source.get("q") || "").trim(),
    universe_scope: universes.length ? "explicit" : "all_enabled",
    universes,
    filters,
    sort: String(source.get("sort") || "relevance"),
  };
}

export function specificationToSearch(specification) {
  const params = new URLSearchParams();
  if (specification?.text) params.set("q", specification.text);
  (specification?.universes || []).forEach((universe) => params.append("universe", universe));
  Object.entries(specification?.filters || {}).forEach(([key, value]) => {
    if (FILTER_KEYS.includes(key) && value != null && String(value).trim()) params.set(key, String(value));
  });
  if (specification?.sort && specification.sort !== "relevance") params.set("sort", specification.sort);
  return `/recherche${params.toString() ? `?${params.toString()}` : ""}`;
}
